import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  useTheme,
} from "@mui/material";
import LoginForm from "../../components/LoginForm";
import emailAnimation from "../../lotties/email.json";
import emailSentAnimation from "../../lotties/email-sent.json";
import Lottie from "lottie-react";
import { LoginFormData } from "../../components/LoginForm";
import { authService } from "../../services/auth/authService";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUser } from "../../context/UserConext";
import { decodeJWT } from "../../utils/jwt";
import { useNavigate, useSearchParams } from "react-router-dom";
import { theme } from "../../theme/theme";
import SendEmailRecover, {
  SendEmailRecoverData,
} from "../../components/SendEmailRecover";
import { motion } from "framer-motion";
import RecoverPasswordForm, {
  RecoverPasswordData,
} from "../../components/RecoverPasswordForm";

const RecoverPasswordPage = () => {
  const lottieRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState("");
  const [showStep, setShowStep] = useState<
    "send-email" | "success" | "reset-password"
  >("send-email");

  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const onSubmit = async (data: SendEmailRecoverData) => {
    setIsLoading(true);
    try {
      await authService.requestPasswordReset(data.email);
      setShowStep("success");
      setDefaultEmail(data.email);
      setTimeout(() => {
        setShowStep("reset-password");
      }, 5000);
    } catch (error) {
      showSnackbar(
        "Error al enviar el correo para restablecer la contraseña",
        "error",
        5000
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitResetPassword = async (data: RecoverPasswordData) => {
    setIsLoading(true);
    const { confirmNewPassword, ...rest } = data;
    try {
      await authService.resetPassword(rest as any);
      showSnackbar("Contraseña restablecida correctamente", "success", 5000);
      navigate("/login");
    } catch (error) {
      showSnackbar("Error al restablecer la contraseña", "error", 5000);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(
      135deg,
      ${theme.palette.secondary.main},
      ${theme.palette.primary.main},
      ${theme.palette.secondary.main}
    )`,
        backgroundSize: "200% 200%",
        animation: "gradientShift 10s ease-in-out infinite",
        "@keyframes gradientShift": {
          "0%, 100%": {
            backgroundPosition: "0% 50%",
          },
          "50%": {
            backgroundPosition: "100% 50%",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
        },
      }}
    >
      <Card
        sx={{
          width: "450px",
          position: "relative",
          zIndex: 1,
          backdropFilter: "blur(10px)",
          backgroundColor: theme.palette.secondary.light,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
        elevation={0}
      >
        {showStep === "send-email" && (
          <CardContent>
            <Box
              sx={{
                height: 300,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Lottie
                lottieRef={lottieRef}
                style={{ width: 300, height: 300 }}
                animationData={emailAnimation}
                loop={false}
                autoplay={true}
                onComplete={() => {
                  if (lottieRef.current) {
                    lottieRef.current.goToAndStop(0, true);
                  }
                }}
              />
            </Box>
            <SendEmailRecover onSubmit={onSubmit} isLoading={isLoading} />
          </CardContent>
        )}

        {showStep === "success" && (
          <CardContent>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Lottie
                  lottieRef={lottieRef}
                  style={{ width: 300, height: 300 }}
                  animationData={emailSentAnimation}
                  loop={false}
                  autoplay={true}
                />
              </Box>
              <Typography variant="h6" textAlign="center">
                Se ha enviado un correo para restablecer tu contraseña.
                Redireccionando...
              </Typography>
            </motion.div>
          </CardContent>
        )}

        {showStep === "reset-password" && (
          <CardContent>
            <Box
              sx={{
                height: 300,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Lottie
                lottieRef={lottieRef}
                style={{ width: 300, height: 300 }}
                animationData={emailAnimation}
                loop={false}
                autoplay={true}
                onComplete={() => {
                  if (lottieRef.current) {
                    lottieRef.current.goToAndStop(0, true);
                  }
                }}
              />
            </Box>
            <RecoverPasswordForm
              onSubmit={onSubmitResetPassword}
              isLoading={isLoading}
              defaultEmail={defaultEmail}
            />
          </CardContent>
        )}
      </Card>
    </Box>
  );
};

export default RecoverPasswordPage;
